<template>
  <div>
    <!-- Header Section -->
    <div class="row mb-4">
      <div class="col-md-8">
        <div class="d-flex align-items-center">
          <div class="bg-primary-light p-3 rounded-2 me-3">
            <vue-feather type="dollar-sign" class="d-flex justify-content-center align-content-center" stroke="#652aa3" size="24"></vue-feather>
          </div>
          <h3 class="mb-0 fw-bold">Loan Applications</h3>
        </div>
      </div>
      <div class="col-md-4">
        <div v-if="profile.role === 'employee'" class="d-flex justify-content-end gap-3">
          <button
              class="btn btn-primary shadow-sm"
              @click="openTermsModal"
          >
            <vue-feather type="plus" size="18" class="me-1"></vue-feather>
            New Loan Application
          </button>
<!--          <button-->
<!--              class="btn btn-outline-info shadow-sm"-->
<!--              @click="openLoanCalcModal"-->
<!--          >-->
<!--            <vue-feather type="list" size="18" class="me-1"></vue-feather>-->
<!--            Loan Calculator-->
<!--          </button>-->
        </div>
      </div>
    </div>

    <!-- Filters Section -->
    <div class="row mb-4">
      <div class="col-12">
        <Card cardClasses="shadow-sm border-0 h-100 bg-white">
          <div class="d-flex flex-wrap gap-3">
            <div v-if="profile.role !== 'employee'" class="flex-grow-1">
              <label class="form-label">Employee</label>
              <v-select
                  label="name"
                  v-model="filters.employee_id"
                  :reduce="employee => employee.id"
                  :options="employees"
                  class="style-chooser"
                  placeholder="Type to search"
                  @option:selected="filterLoanApplications"
              ></v-select>
            </div>
            <div class="flex-grow-1">
              <label class="form-label">Status</label>
              <select class="form-select" v-model="filters.status" @change="filterLoanApplications" data="">
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            <div class="flex-grow-1">
              <label class="form-label">From Date</label>
              <input type="date" class="form-control" v-model="filters.from_date" @change="filterLoanApplications">
            </div>
            <div class="flex-grow-1">
              <label class="form-label">To Date</label>
              <input type="date" class="form-control" v-model="filters.to_date" @change="filterLoanApplications">
            </div>
          </div>
          <div class="col-md-12 mt-3 text-end">
            <button class="btn btn-outline-secondary" @click="resetFilter">
              <vue-feather type="refresh-cw" size="16" class="me-1"></vue-feather>
              Reset Filters
            </button>
          </div>
        </Card>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <Card cardClasses="shadow-sm border-0 h-100 mb-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <div class="font-size-125 color-gray-600">
              <vue-feather type="dollar-sign" stroke="#652aa3" size="18" class="me-1"></vue-feather>
              Loan Applications ({{loanApplications.length}})
            </div>
            <UInput v-model="dataTableSearch" size="md" placeholder="Search loan application" class="mb-0 w-25 search-input"/>
          </div>

          <!-- Loading Spinner -->
          <div v-if="loading" class="text-center py-4">
            <Spinner spinner-class="mb-1" />
          </div>

          <!-- Loan Applications Table -->
          <div v-if="!loading && loanApplications.length > 0" class="table-responsive">
            <table class="table table-hover align-middle">
              <thead class="table-light">
              <tr>
                <th scope="col" class="fw-semibold">Employee</th>
                <th scope="col" class="fw-semibold">Amount</th>
                <th scope="col" class="fw-semibold">Purpose</th>
                <th scope="col" class="fw-semibold">Status</th>
                <th scope="col" class="fw-semibold">Applied On</th>
                <th scope="col" class="text-end">Actions</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(application, index) in filteredLoanApplications" :key="application.id">
                <td>
                  <a :href="`/employee/${application.employee.id}`" class="fw-semibold">
                    {{ application.employee.first_name }} {{ application.employee.middle_name }} {{ application.employee.last_name }}
                  </a>
                </td>
                <td>{{ formatNumber(application.amount) }}</td>
                <td>{{ application.purpose }}</td>
                <td>
                    <span :class="getStatusBadgeClass(application.status)">
                      {{ capitalizeFirstLetter(application.status) }}
                    </span>
                </td>
                <td>{{ formatDate(application.created_at) }}</td>
                <td class="text-end">
                  <button class="btn btn-sm btn-outline-primary me-1" @click="viewLoanApplication(application)">
                    <vue-feather type="eye" size="14"></vue-feather>
                  </button>
                </td>
              </tr>
              </tbody>
            </table>
          </div>

          <!-- Empty State -->
          <div v-if="!loading && loanApplications.length === 0 && profile.role === 'employee'" class="text-center py-5">
            <vue-feather type="dollar-sign" size="48" class="mb-3" stroke="#652aa3"></vue-feather>
            <h5>No Loan Applications Found</h5>
            <p class="text-muted">Create your first loan application to get started.</p>
            <button class="btn btn-primary" @click="openTermsModal">
              <vue-feather type="plus" size="16" class="me-1"></vue-feather>
              New Loan Application
            </button>
          </div>
        </Card>
      </div>
    </div>

    <!-- Loan Application Modal -->
    <Modal
        dialog-size="modal-lg"
        :open="showInitializeModal"
        @closed="closeInitializeModal"
    >
      <div class="modal-header border-0">
        <h5 class="modal-title m-0 d-flex align-items-center">
          Loan Application
        </h5>
        <button @click="closeInitializeModal" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="">
          <div class="row">
            <div class="mb-3 col-md-6">
              <label for="guarantor_1" class="form-label">Guarantor 1 <span class="text-danger">*</span></label>
              <v-select
                  label="name"
                  v-model="initializeForm.guarantor_1"
                  :reduce="employee => employee.id"
                  :options="employees"
                  class="style-chooser"
                  placeholder="Type to search"
                  @option:selected="guarantorChecks('guarantor_1')"
              ></v-select>
            </div>
            <div class="mb-3 col-md-6">
              <label for="guarantor_2" class="form-label">Guarantor 2 <span class="text-danger">*</span></label>
              <v-select
                  label="name"
                  v-model="initializeForm.guarantor_2"
                  :reduce="employee => employee.id"
                  :options="employees"
                  class="style-chooser"
                  placeholder="Type to search"
                  @option:selected="guarantorChecks('guarantor_2')"
              ></v-select>
            </div>
            <div class="mb-3 col-md-6">
              <label for="product_id" class="form-label">Loan Product <span class="text-danger">*</span></label>
              <v-select
                  label="label"
                  v-model="initializeForm.product_id"
                  :reduce="product => product.product_id"
                  :options="productOptions"
                  class="style-chooser"
                  placeholder="Select a product"
                  @option:selected="onProductSelected"
              ></v-select>
              <small v-if="selectedProduct" class="text-muted d-block">Product range: {{ formatNumber(selectedProduct.amount_min) }} – {{ formatNumber(selectedProduct.amount_max) }}</small>
            </div>
            <div class="mb-3 col-md-6">
              <label for="term_months" class="form-label">Repayment Term (months) <span class="text-danger">*</span></label>
              <select class="form-select" id="term_months" v-model="initializeForm.term_months" required data="">
                <option value="" disabled>Select months</option>
                <option v-for="m in possibleTerms" :key="m" :value="m">{{ m }}</option>
              </select>
            </div>
            <div class="mb-3 col-md-6">
              <label for="amount" class="form-label">Amount <span class="text-danger">*</span></label>
              <input type="number" class="form-control" id="amount" v-model="initializeForm.amount" required :max="maxLoanAmount">
              <small v-if="maxLoanAmount > 0" class="text-muted">Maximum loan amount: {{ formatNumber(maxLoanAmount) }}</small>
            </div>
            <div class="mb-3 col-md-6">
              <label for="purpose" class="form-label">Purpose <span class="text-danger">*</span></label>
              <select id="purpose" class="form-select" v-model="purposeSelect" required data="">
                <option value="" disabled>Select purpose</option>
                <option value="Medical">Medical</option>
                <option value="Education/School fees">Education/School fees</option>
                <option value="Rent">Rent</option>
                <option value="Relocation">Relocation</option>
                <option value="Emergency">Emergency</option>
                <option value="Travel">Travel</option>
                <option value="Purchase of appliance">Purchase of appliance</option>
                <option value="Car repair">Car repair</option>
                <option value="Funeral expense">Funeral expense</option>
                <option value="Legal expense">Legal expense</option>
                <option value="Buying land/property">Buying land/property</option>
                <option value="Car/Asset purchase">Car/Asset purchase</option>
                <option value="Property down payment">Property down payment</option>
                <option value="Other">Other</option>
              </select>
              <input v-if="purposeSelect === 'Other'" type="text" class="form-control mt-2" v-model="otherPurpose" placeholder="Enter purpose" />
            </div>
            <!-- Repayment info -->
            <div v-if="repaymentInfo.total" class="alert alert-info col-12">
              <div class="mb-2 col-6"><strong>Total Payable:</strong> {{ formatNumber(repaymentInfo.total) }}</div>
              <div class="mb-2 col-6"><strong>Monthly Payment:</strong> {{ formatNumber(repaymentInfo.monthly) }}</div>
            </div>
            <div class="d-flex justify-content-end">
              <button type="button" class="btn btn-outline-secondary me-2" @click="closeInitializeModal">Cancel</button>
              <button type="submit" class="btn btn-primary" @click="submitLoanApplication" :class="saving ? 'disabled' : ''">
                <span v-if="saving" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                Apply Loan
              </button>
            </div>
          </div>
        </form>
      </div>
    </Modal>

    <!-- Loan Terms Modal -->
    <Modal
        :open="showTermsModal"
        @closed="declineTerms"
    >
      <div class="modal-header border-0">
        <h5 class="modal-title m-0 d-flex align-items-center">
          Loan Terms &amp; Conditions
        </h5>
        <button @click="declineTerms" type="button" class="btn-close outline-none" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <p>
          By this clause I authorise my employer <strong class="text-primary h5">{{ organization.name }}</strong> to make deductions from my salary, plus any other accrued charges to Aleza Limited from my final dues, should I lose my employment or terminate my services with the employer named above.
        </p>
        <p>
          If the final salary and benefits are insufficient to cover the outstanding loan balance, the borrower agrees to settle the remaining balance through alternative means.
        </p>
        <p>
          By clicking "Accept" below, I agree to all terms and conditions of this agreement as listed.
        </p>
        <div class="d-flex justify-content-end mt-4">
          <button type="button" class="btn btn-outline-secondary me-2" @click="declineTerms">Decline</button>
          <button type="button" class="btn btn-primary" @click="acceptTerms">Accept</button>
        </div>
      </div>
    </Modal>

    <!-- Loan Calculator Modal -->
    <Modal
        :open="showLoanCalcModal"
        @closed="closeLoanCalcModal"
    >
      <div class="modal-header border-0">
        <h5 class="modal-title m-0 d-flex align-items-center">
          Loan Calculator
        </h5>
        <button @click="closeLoanCalcModal" type="button" class="btn-close outline-none" aria-label="Close"></button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="submitLoanCalc">
          <div class="mb-3">
            <label class="form-label">Loan Product <span class="text-danger">*</span></label>
            <v-select
                label="label"
                v-model="loanCalcForm.product_id"
                :reduce="product => product.product_id"
                :options="productOptions"
                class="style-chooser"
                placeholder="Select a product"
                @option:selected="onCalcProductSelected"
            ></v-select>
          </div>
          <div class="mb-3">
            <label class="form-label">Amount <span class="text-danger">*</span></label>
            <input
                type="number"
                class="form-control"
                v-model="loanCalcForm.amount"
                :min="calcSelectedProduct ? calcSelectedProduct.amount_min : null"
                :max="calcSelectedProduct ? calcSelectedProduct.amount_max : null"
                required
            />
            <small v-if="calcSelectedProduct" class="text-muted d-block">
              Range: {{ formatNumber(calcSelectedProduct.amount_min) }} – {{ formatNumber(calcSelectedProduct.amount_max) }}
            </small>
          </div>
          <div class="mb-3" v-if="calcPossibleTerms.length">
            <label class="form-label">Period (months) <span class="text-danger">*</span></label>
            <select class="form-select" v-model="loanCalcForm.period" required data="">
              <option value="" disabled>Select months</option>
              <option v-for="m in calcPossibleTerms" :key="m" :value="m">{{ m }}</option>
            </select>
          </div>
          <div class="d-flex justify-content-end">
            <button type="button" class="btn btn-outline-secondary me-2" @click="closeLoanCalcModal">Close</button>
            <button type="submit" class="btn btn-primary" :class="calcLoading ? 'disabled' : ''">
              <span v-if="calcLoading" class="spinner-border spinner-border-sm me-1"></span>
              Calculate
            </button>
          </div>
        </form>
        <div v-if="quotationResult" class="card shadow-sm border-0 mt-4">
          <div class="card-body">
            <h5 class="card-title mb-4 d-flex align-items-center gap-2">
              <vue-feather type="trending-up" size="18" class="text-primary"></vue-feather>
              {{ quotationResult.productName }} Quotation
            </h5>
            <div class="row text-center gy-3">
              <div class="col-6">
                <small class="text-muted">Principal</small>
                <div class="h5 mb-0">{{ formatNumber(quotationResult.principal) }}</div>
              </div>
              <div class="col-6">
                <small class="text-muted">Total Payable</small>
                <div class="h5 mb-0">{{ formatNumber(quotationResult.totalAmount) }}</div>
              </div>
            </div>
            <hr>
            <div class="row text-center gy-3">
              <div class="col-6">
                <small class="text-muted">Installments</small>
                <div class="h5 mb-0">{{ quotationResult.installmentCount }}</div>
              </div>
              <div class="col-6">
                <small class="text-muted">Monthly Installment</small>
                <div class="h5 mb-0">{{ formatNumber(quotationResult.monthlyInstallment) }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>

  </div>
</template>

<script>
import loanService from '@/api/loanService';
import {decrypt} from '@/utils/crypto';
import notify from '@/utils/notify';
import formatDate from '@/utils/formatDate';
import formatNumber from "@/utils/formatNumber";
import VueFeather from 'vue-feather';
import confirmDialog from "@/utils/confirmDialog";
import selectSearch from "@/utils/selectSearch";
import organization from "../organization/Organization.vue";
import payrollService from "@/api/payrollService";

export default {
  name: 'LoanApplications',
  components: { VueFeather },
  data() {
    return {
      loading: true,
      saving: false,
      loanApplications: [],
      profile: {},
      currentEmployee: {},
      employees: [],
      dataTableSearch: '',
      filters: {
        employee_id: '',
        status: '',
        from_date: '',
        to_date: ''
      },
      showInitializeModal: false,
      showTermsModal: false,
      showRejectModal: false,
      showApproveModal: false,
      showLoanCalcModal: false,
      selectedApplication: null,
      initializeForm: {
        product_id: '',
        code: '',
        guarantor_1: '',
        guarantor_2: '',
        amount: '',
        purpose: '',
        term_months: 1
      },
      rejectForm: {
        rejection_reason: ''
      },
      approveForm: {
        comments: ''
      },
      loanCalcForm: {
        product_id: '',
        amount: '',
        period: ''
      },
      maxLoanAmount: 0,
      productOptions: [
        {
          product_id: 'NSUbRBkIuIoPgeMc',
          label: 'Check-Off Loan (COL)',
          code: 'ALEZA COL LOAN',
          name: 'Check-Off Loan',
          fees: 'Processing fee: 5%, Insurance fee: 2%',
          interest_method: 'Flat rate',
          interest_rates: 'Graduated rates – 6.6% for 1–6 mo, 5.6% for 7–9 mo, 4.6% for 10–12 mo',
          term_range: '1 – 12 months',
          amount_min: 8000,
          amount_max: 350000,
          term_min: 1,
          term_max: 12,
          processing_fee_pct: 5,
          insurance_fee_pct: 2,
          interest_rates_map: [
            {min:1,max:6,rate:6.6},
            {min:7,max:9,rate:5.6},
            {min:10,max:12,rate:4.6}
          ]
        },
        {
          product_id: 'PPHYu5mv8f0ITKsY',
          label: 'Aleza Water Tank Loan (AWT)',
          code: 'ALEZA AWT LOAN',
          name: 'Aleza Water Tank Loan',
          fees: 'No up-front fees',
          interest_method: 'Flat rate',
          interest_rates: '20% flat',
          term_range: '1 – 3 months',
          amount_min: 12000,
          amount_max: 37000,
          term_min: 1,
          term_max: 3,
          processing_fee_pct: 0,
          insurance_fee_pct: 0,
          interest_rate_flat: 20
        }
      ],
      selectedProduct: null,
      possibleTerms: [],
      repaymentInfo: {
        total: 0,
        monthly: 0
      },
      calcLoading: false,
      quotationResult: null,
      calcPossibleTerms: [],
      calcSelectedProduct: null,
      purposeSelect: '',
      otherPurpose: '',
      lastPayslip: {}
    };
  },

  methods: {
    formatDate,
    formatNumber,
    confirmDialog,
    capitalizeFirstLetter(string) {
      return string.charAt(0).toUpperCase() + string.slice(1);
    },
    loadCurrentEmployee() {
      if (!this.profile.employee_id) {
        return;
      }
      this.currentEmployee = this.employees.find(employee => employee.id === this.profile.employee_id);
      this.calculateMaxLoanAmount();
    },
    calculateMaxLoanAmount() {
      this.maxLoanAmount = Math.round((this.lastPayslip.net ?? 0) * (2/3) / 1000) * 1000 * this.initializeForm.term_months
    },
    getStatusBadgeClass(status) {
      switch (status) {
        case 'pending':
          return 'badge bg-warning';
        case 'approved':
          return 'badge bg-success';
        case 'rejected':
          return 'badge bg-danger';
        default:
          return 'badge bg-primary';
      }
    },
    async fetchLoanApplications() {
      try {
        this.loading = true;
        this.loanApplications = await loanService.getLoanApplications();
      } catch (error) {
        notify.alert.error('Failed to fetch loan applications');
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    async fetchLastPayslip() {
      this.loading = true
      this.lastPayslip = await payrollService.getLastPayslip(this.profile.employee_id)
      this.loading = false
    },
    async filterLoanApplications() {
      try {
        this.loading = true;
        let filtered = await loanService.getLoanApplications();

        // Employee filter (only for non-employee roles)
        if (this.filters.employee_id) {
          filtered = filtered.filter(app => app.employee_id === this.filters.employee_id);
        }

        // Status filter
        if (this.filters.status) {
          filtered = filtered.filter(app => app.status === this.filters.status);
        }

        // Date range filter
        if (this.filters.from_date) {
          const from = new Date(this.filters.from_date);
          filtered = filtered.filter(app => new Date(app.created_at) >= from);
        }
        if (this.filters.to_date) {
          const to = new Date(this.filters.to_date);
          filtered = filtered.filter(app => new Date(app.created_at) <= to);
        }

        this.loanApplications = filtered;
      } catch (error) {
        notify.alert.error('Failed to filter loan applications');
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    async fetchEmployees() {
      this.employees = await selectSearch.allEmployees()
    },
    async guarantorChecks(guarantor) {
      if (this.initializeForm[guarantor] === '') return;

      // Prevent selecting same guarantor twice
      const selectedGuarantorId = this.initializeForm[guarantor];
      const otherGuarantorKey = guarantor === 'guarantor_1' ? 'guarantor_2' : 'guarantor_1';
      if (this.initializeForm[otherGuarantorKey] && this.initializeForm[otherGuarantorKey] === selectedGuarantorId) {
        notify.alert.error('You have already selected this guarantor');
        this.initializeForm[guarantor] = '';
        return;
      }

      // Prevent selecting yourself as guarantor
      if (selectedGuarantorId === this.profile.employee_id) {
        notify.alert.error('You cannot select yourself as a guarantor');
        this.initializeForm[guarantor] = '';
        return;
      }

      const guarantorEmployee = this.employees.find(employee => employee.id === selectedGuarantorId);
      if (guarantorEmployee && guarantorEmployee.salary < (this.currentEmployee.salary * 0.4)) {
        notify.alert.error("Guarantor's salary is not eligible for your loan");
        this.initializeForm[guarantor] = '';
      }

      // Check if guarantor is active
      if (guarantorEmployee && guarantorEmployee.status !== 'active') {
        notify.alert.error('Guarantor is not active');
        this.initializeForm[guarantor] = '';
      }
    },
    openTermsModal() {
      this.showTermsModal = true;
    },
    acceptTerms() {
      this.showTermsModal = false;
      // proceed to initialize loan application modal
      this.openInitializeModal();
    },
    declineTerms() {
      this.showTermsModal = false;
    },
    openInitializeModal() {
      this.initializeForm = {
        product_id: '',
        code: '',
        guarantor_1: '',
        guarantor_2: '',
        amount: '',
        purpose: '',
        term_months: ''
      };
      this.purposeSelect = '';
      this.otherPurpose = '';
      this.showInitializeModal = true;
    },
    closeInitializeModal() {
      this.showInitializeModal = false;
    },
    viewLoanApplication(application) {
      this.$router.push(`/loan/${application.id}`);
    },
    async resetFilter() {
      window.location.reload()
    },
    onProductSelected(product) {
      this.selectedProduct = this.productOptions.find(p => p.product_id === product.product_id);
      if (this.selectedProduct) {
        // prepare range of months for term selector
        const min = this.selectedProduct.term_min;
        const max = this.selectedProduct.term_max;
        this.possibleTerms = Array.from({length: (max - min + 1)}, (_, i) => i + min);
        this.initializeForm.term_months = '';
      }
      this.calculateRepayment();
    },
    async submitLoanApplication() {
      if (!this.initializeForm.product_id || !this.initializeForm.guarantor_1 || !this.initializeForm.guarantor_2 || !this.initializeForm.amount || !this.initializeForm.purpose || !this.initializeForm.term_months) {
        notify.alert.error('Please fill all required fields');
        return;
      }

      // validate amount range for selected product
      if (this.selectedProduct) {
        if (this.initializeForm.amount < this.selectedProduct.amount_min || this.initializeForm.amount > this.selectedProduct.amount_max) {
          notify.alert.error(`Product amount must be between ${this.formatNumber(this.selectedProduct.amount_min)} and ${this.formatNumber(this.selectedProduct.amount_max)}`);
          return;
        }
      }

      // validate term range
      if (this.selectedProduct) {
        if (this.initializeForm.term_months < this.selectedProduct.term_min || this.initializeForm.term_months > this.selectedProduct.term_max) {
          notify.alert.error(`Term must be between ${this.selectedProduct.term_min} and ${this.selectedProduct.term_max} months`);
          return;
        }
      }

      try {
        this.saving = true;
        this.initializeForm.organization_id = this.currentEmployee.organization_id
        this.initializeForm.employee_id = this.currentEmployee.id
        this.initializeForm.code = this.selectedProduct.code
        this.initializeForm.interest_amount = this.repaymentInfo.total - this.initializeForm.amount
        let response = await loanService.submitLoanApplication(this.initializeForm);
        if (response) {
          this.closeInitializeModal();
          await this.fetchLoanApplications();
        }
      } catch (error) {
        notify.alert.error(error.response?.data?.message || 'Failed to initialize loan application');
        console.error(error);
      } finally {
        this.saving = false;
      }
    },
    onCalcProductSelected(product) {
      this.calcSelectedProduct = this.productOptions.find(p => p.product_id === product.product_id);
      if (this.calcSelectedProduct) {
        const min = this.calcSelectedProduct.term_min;
        const max = this.calcSelectedProduct.term_max;
        this.calcPossibleTerms = Array.from({length: (max - min + 1)}, (_, i) => i + min);
        this.loanCalcForm.period = '';
      }
      this.loanCalcForm.amount = '';
    },
    calculateRepayment() {
      // Maximum loan amount
      this.calculateMaxLoanAmount();

      if (!this.selectedProduct || !this.initializeForm.amount || !this.initializeForm.term_months) {
        this.repaymentInfo = {total: 0, monthly: 0};
        return;
      }

      const principal = parseFloat(this.initializeForm.amount);
      // fees
      const procFee = (this.selectedProduct.processing_fee_pct || 0) * principal / 100;
      const insFee = (this.selectedProduct.insurance_fee_pct || 0) * principal / 100;
      // interest
      let interestRate = 0;
      if (this.selectedProduct.interest_rate_flat !== undefined) {
        interestRate = this.selectedProduct.interest_rate_flat;
      } else if (this.selectedProduct.interest_rates_map) {
        const term = this.initializeForm.term_months;
        const bracket = this.selectedProduct.interest_rates_map.find(b => term >= b.min && term <= b.max);
        if (bracket) interestRate = bracket.rate;
      }
      const interest = principal * (interestRate / 100) * this.initializeForm.term_months;
      const total = principal + procFee + insFee + interest;
      const monthly = total / this.initializeForm.term_months;
      this.repaymentInfo = {total: Math.round(total), monthly: Math.round(monthly)};
    },
    openLoanCalcModal() {
      this.showLoanCalcModal = true;
    },
    closeLoanCalcModal() {
      this.showLoanCalcModal = false;
      this.calcLoading = false;
      this.quotationResult = null;
      this.loanCalcForm = {product_id: '', amount: '', period: ''};
      this.calcPossibleTerms = [];
    },
    async submitLoanCalc() {
      if (!this.loanCalcForm.product_id || !this.loanCalcForm.amount || !this.loanCalcForm.period) {
        notify.alert.error('Please fill all required fields');
        return;
      }
      try {
        this.calcLoading = true;
        const payload = {
          employee_id: this.profile.employee_id,
          loanProductRefId: this.loanCalcForm.product_id,
          amount: this.loanCalcForm.amount,
          period: this.loanCalcForm.period,
        }
        this.quotationResult = await loanService.generateLoanQuotation(payload);
      } catch (e) {
        notify.alert.error(e.response?.data?.message || 'Failed to generate quotation');
      } finally {
        this.calcLoading = false;
      }
    },
  },

  computed: {
    filteredLoanApplications() {
      if (!this.dataTableSearch) return this.loanApplications;

      const search = this.dataTableSearch.toLowerCase();
      return this.loanApplications.filter(application =>
          (application.employee.first_name + ' ' + application.employee.last_name).toLowerCase().includes(search) ||
          application.purpose.toLowerCase().includes(search) ||
          application.status.toLowerCase().includes(search)
      );
    },
  },

  async created() {
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')));
    this.organization = JSON.parse(decrypt(sessionStorage.getItem('organization')));
    await this.fetchLastPayslip()
    await this.fetchLoanApplications();
    this.fetchEmployees().then(() => {
      this.loadCurrentEmployee();
    });
  },

  watch: {
    'initializeForm.amount': 'calculateRepayment',
    'initializeForm.term_months': 'calculateRepayment',
    'initializeForm.product_id': 'calculateRepayment',
    purposeSelect(val) {
      if (val === 'Other') {
        this.initializeForm.purpose = this.otherPurpose;
      } else {
        this.initializeForm.purpose = val;
      }
    },
    otherPurpose(val) {
      if (this.purposeSelect === 'Other') {
        this.initializeForm.purpose = val;
      }
    }
  },
};
</script>
